import { useState } from "react";
import { useReveal } from "../../hooks/useReveal";
import { useI18n } from "../../i18n/useI18n";
import { useJsonLd } from "../../lib/seo";

export function FAQ() {
  const scope = useReveal<HTMLElement>();
  const { t } = useI18n();
  const [open, setOpen] = useState<number | null>(0);

  // FAQPage structured data — one of the Phase 1 E-E-A-T/GEO gaps flagged
  // in docs/seo-trust-audit.md section 2.2 ("Nicht ergänzt: ... FAQPage").
  // Rendered here (not in index.html) because the FAQ content itself is
  // per-language (useI18n) and only exists once this component mounts on
  // Home — see useJsonLd's header for why this is safe alongside
  // I18nProvider's title/description ownership of Home.
  useJsonLd("faq", {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: t.faq.items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  });

  return (
    <section id="faq" ref={scope} className="py-24 md:py-32">
      <div className="container-lux max-w-3xl">
        <div className="text-center">
          <p className="eyebrow mb-4" data-reveal>{t.faq.eyebrow}</p>
          <h2 className="text-3xl font-semibold md:text-5xl" data-reveal>{t.faq.title}</h2>
        </div>
        <div className="mt-14 space-y-3">
          {t.faq.items.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={item.q} data-reveal className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white/80 shadow-sm backdrop-blur-md">
                <button
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left font-display text-lg"
                >
                  <span>{item.q}</span>
                  <span className={`shrink-0 text-gold transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`}>+</span>
                </button>
                <div className={`grid transition-all duration-300 ease-lux ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                  <div className="overflow-hidden">
                    <p className="px-6 pb-5 text-slate2/80">{item.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
